/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, February 2022
 * @format
 * @flow
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { Divider } from '../Divider';
import CheckCard from '../CheckCard';
import FireboardUtils from '../../../../util/fireboard-util';
import modeEnums from '../../../../enums/modeEnums';
import classes from './ChecksWithGroup.module.css';
import CommonUtil from '../../../Fireboard/common';

const SOURCETYPE = 'sourceType';
const ORDERTYPE = 'orderType';
const REVENUECENTER = 'revenueCenter';
const SENSORGROUP = 'sensorGroup';

const SOURCETYPE_ARRAY = ['Curbside', 'Online', 'Walk up', 'Call in', 'Kiosk', 'Scan to pay', 'POS'];

const ChecksWithGroup = props => {
  const {
    checks,
    onSelectCheck,
    selectedCheck,
    showPopup,
    currentMode,
    onClickCheckFooterButton,
    checkFilters,
    checkSortFunction,
    ascending,
    checkIdsMap,
    sensorsMap,
  } = props;
  const [groupChecks, setGroupChecks] = useState(new Map());
  const { data: storeInfo } = useSelector(state => state.storeInfo);

  const filterChecks = () => {
    const { activeCheckFilter } = checkFilters;
    let checksMap = new Map();
    switch (activeCheckFilter) {
      case SOURCETYPE:
        checksMap = filterBasedOnSourceType();
        break;
      case ORDERTYPE:
        checksMap = filterBasedOnOrderType();
        break;
      case REVENUECENTER:
        checksMap = filterBasedOnRevenueCenter();
        break;
      case SENSORGROUP:
        checksMap = filterBasedOnSensorGroup();
        break;
      default: break;
    }
    setGroupChecks(checksMap);
  };

  const filterBasedOnSourceType = () => {
    const { orderType, checkSourceTypes } = checkFilters;
    let checksMap = new Map();
    if (orderType === 'All') {
      if (checkSourceTypes && Array.isArray(checkSourceTypes) && checkSourceTypes.length) {
        //remove 'All' label from list - if available
        const selectedTypes = checkSourceTypes.filter(type => type !== 'All');
        selectedTypes.forEach(srcTypeName => checksMap.set(srcTypeName, []));
      } else
        SOURCETYPE_ARRAY.forEach(srcTypeName => checksMap.set(srcTypeName, []));

      checks.forEach(check => {
        const sourceTypeName = FireboardUtils.getOrderTypeFromSourceType(check.sourceType);
        if (sourceTypeName) {
          const existingChecks = checksMap.has(sourceTypeName) ? checksMap.get(sourceTypeName) : [];
          const newChecks = [...existingChecks, check];
          checksMap.set(sourceTypeName, newChecks);
        }
      });
    }

    return checksMap;
  };

  const filterBasedOnOrderType = () => {
    const { orderType, checkOrderTypes } = checkFilters;
    let checksMap = new Map();
    if (orderType === 'All') {
      const { orderTypes } = storeInfo;
      let orderTypesLookup = {};
      orderTypes.forEach(eachType => orderTypesLookup[eachType.id] = eachType.name);

      const sortedOrderTypeNamesArr = getSortedRefsArray(orderTypes);
      if (checkOrderTypes && Array.isArray(checkOrderTypes) && checkOrderTypes.length) {
        //remove 'All' label from list - if available
        const selectedTypes = checkOrderTypes.filter(type => type !== 'All');
        selectedTypes.sort((a, b) => (
          sortedOrderTypeNamesArr.indexOf(a) - sortedOrderTypeNamesArr.indexOf(b)
        ));
        selectedTypes.forEach(odrTypeName => checksMap.set(odrTypeName, []));
      } else {
        sortedOrderTypeNamesArr.forEach(odrTypeName => checksMap.set(odrTypeName, []));
      }

      checks.forEach(check => {
        const orderTypeName = orderTypesLookup[check.orderTypeId];
        if (orderTypeName) {
          const existingChecks = checksMap.has(orderTypeName) ? checksMap.get(orderTypeName) : [];
          const newChecks = [...existingChecks, check];
          checksMap.set(orderTypeName, newChecks);
        }
      });
    }

    return checksMap;
  };

  const filterBasedOnRevenueCenter = () => {
    const { orderType, checkRevenueTypes } = checkFilters;
    let checksMap = new Map();
    if (orderType === 'All') {
      let { revenueCenters } = storeInfo;
      let revenueCentersLookup = {};
      revenueCenters.forEach(eachType => revenueCentersLookup[eachType.id] = eachType.name);

      const sortedRevCenterNamesArr = getSortedRefsArray(revenueCenters);
      if (checkRevenueTypes && Array.isArray(checkRevenueTypes) && checkRevenueTypes.length) {
        //remove 'All' label from list - if available
        const selectedTypes = checkRevenueTypes.filter(type => type !== 'All');
        selectedTypes.sort((a, b) => (
          sortedRevCenterNamesArr.indexOf(a) - sortedRevCenterNamesArr.indexOf(b)
        ));
        selectedTypes.forEach(rCenterName => checksMap.set(rCenterName, []));
      } else {
        sortedRevCenterNamesArr.forEach(rCenterName => checksMap.set(rCenterName, []));
      }

      checks.forEach(check => {
        const revenueCenterName = revenueCentersLookup[check.revenueCenterId];
        if (revenueCenterName) {
          const existingChecks = checksMap.has(revenueCenterName) ? checksMap.get(revenueCenterName) : [];
          const newChecks = [...existingChecks, check];
          checksMap.set(revenueCenterName, newChecks);
        }
      });
    }

    return checksMap;
  };

  const filterBasedOnSensorGroup = () => {
    const { orderType, checkSensorGroupTypes } = checkFilters;
    let checksMap = new Map();
    if (orderType === 'All') {
      const { sensorGroups } = storeInfo;
      let sensorGroupLookup = new Map();
      if (checkSensorGroupTypes && Array.isArray(checkSensorGroupTypes) && checkSensorGroupTypes.length > 0) {
        //remove 'All' label from list - if available
        const selectedTypes = checkSensorGroupTypes.filter(type => type !== 'All');
        selectedTypes.sort(FireboardUtils.sortByValue);
        selectedTypes.forEach(sensorGrpName => {
          const hasSensorGroup = sensorGroups.find(sensorGrp => (sensorGrp.name).toLowerCase().trim() === sensorGrpName.toLowerCase().trim());
          if (hasSensorGroup && hasSensorGroup?.id) {
            sensorGroupLookup.set(sensorGrpName, hasSensorGroup.id);
            checksMap.set(sensorGrpName, []);
          }
        });
      } else {
        const sensorGroupList = [...sensorGroups];
        sensorGroupList.sort(FireboardUtils.sortByName);
        sensorGroupList.forEach(sensorGrp => {
          sensorGroupLookup.set(sensorGrp.name, sensorGrp.id);
          checksMap.set(sensorGrp.name, []);
        });
      }

      Array.from(sensorGroupLookup.entries()).map(([key, value]) => {
        const checkArr = CommonUtil.getSensorGroupedChecks(checks, sensorsMap, [value]);
        if (checkArr && Array.isArray(checkArr) && checkArr.length) {
          const existingChecks = checksMap.has(key) ? checksMap.get(key) : [];
          const newChecks = [...existingChecks, ...checkArr];
          checksMap.set(key, newChecks);
        }
      });
    }

    return checksMap;
  };

  useEffect(() => {
    filterChecks();
  }, [checks, checkFilters]);

  const sortBySortOrderKey = (a, b) => {
    const sortOrder1 = a.sortOrder;
    const sortOrder2 = b.sortOrder;
    return sortOrder1 - sortOrder2;
  };

  const getSortedRefsArray = referenceArray => {
    const refsArray = referenceArray || [];
    refsArray.sort(sortBySortOrderKey);
    const refsArrayNames = refsArray.map(eachItem => eachItem.name);
    return refsArrayNames;
  };

  const sortCheckArray = checkArray => {
    const sortedArray = [...checkArray];
    sortedArray.sort(checkSortFunction);
    if (!ascending)
      sortedArray.reverse();

    if (currentMode === modeEnums.TABLE_DELIVERY) {
      const arrangedArrBasedOnVehicleInfo = [
        ...sortedArray.filter(check => {
          const [order0] = check?.orders || [];
          return order0?.vehicleInfo;
        }),
        ...sortedArray.filter(check => {
          const [order0] = check?.orders || [];
          return !order0?.vehicleInfo;
        }),
      ];
      return arrangedArrBasedOnVehicleInfo;
    }

    return sortedArray;
  };

  return (
    <>
      {Array.from(groupChecks.entries()).map(([key, checkArray]) =>
        <React.Fragment key={key}>
          <Divider title={key} />
          {checkArray?.length === 0 ? <NoSectionCheck /> : (
            <div className={classes.checkCardsWrapper}>
              {sortCheckArray(checkArray).map(check =>
                <CheckCard
                  key={check.id}
                  check={check}
                  onSelectCheck={onSelectCheck}
                  isCheckSelected={selectedCheck.id === check.id}
                  showPopup={showPopup}
                  currentMode={currentMode}
                  onClickCheckFooterButton={onClickCheckFooterButton}
                  checkIdsMap={checkIdsMap}
                />
              )}
            </div>
          )}
        </React.Fragment>
      )}
    </>
  );
};

export default ChecksWithGroup;

ChecksWithGroup.propTypes = {
  checks: PropTypes.array.isRequired,
  onSelectCheck: PropTypes.func,
  selectedCheck: PropTypes.object,
  showPopup: PropTypes.func,
  currentMode: PropTypes.string,
  onClickCheckFooterButton: PropTypes.func,
  checkFilters: PropTypes.object,
  checkSortFunction: PropTypes.func,
  ascending: PropTypes.bool,
  checkIdsMap: PropTypes.instanceOf(Map),
  sensorsMap: PropTypes.instanceOf(Map),
};

ChecksWithGroup.defaultProps = {
  checks: [],
  onSelectCheck: () => { },
  selectedCheck: {},
  showPopup: () => { },
  currentMode: modeEnums.TABLE_DELIVERY,
  onClickCheckFooterButton: () => { },
  checkFilters: {},
  checkSortFunction: () => { },
  ascending: true,
  checkIdsMap: new Map(),
  sensorsMap: new Map(),
};

const NoSectionCheck = () => (
  <div className={classes.noSectionCheck}>
    <span>No checks</span>
  </div>
);
